import axios from "axios";
import { getApi, getPagination } from "./utils";

function formatDictionary(data) {
    if (!data) return [];

    return data.map((item) => ({ id: item.id, name: item.name }));
}

export default {
    namespaced: true,

    state: {
        workers: [],
        users: [],
        dictionaries: {},
    },

    getters: {
        workersList(state) {
            return state.workers.map((worker) => {
                const { id, name } = worker;
                return {
                    value: id,
                    label: name,
                };
            });
        },
        activeActorsList(state) {
            const workers = state.workers.map((worker) => {
                const { id, name, user_id } = worker;
                return {
                    value: `worker-${id}`,
                    workerId: id,
                    userId: user_id,
                    label: name,
                };
            });
            const usersWithoutWorker = state.users
                .filter((user) => !user.worker)
                .map((userData) => {
                    const { id, name } = userData.user;
                    return {
                        value: `user-${id}`,
                        workerId: null,
                        userId: id,
                        label: name,
                    };
                });

            const actors = [...workers, ...usersWithoutWorker];

            return actors;
        },
        getDictionaries(state) {
            return state.dictionaries;
        },
    },

    mutations: {
        setWorkers(state, list) {
            state.workers = list;
        },
        setUsers(state, list) {
            state.users = list;
        },
        setDictionary(state, { code, list }) {
            state.dictionaries = {
                ...state.dictionaries,
                [code]: list,
            };
        },
        addDictionaryItems(state, { code, list }) {
            const dictionary = state.dictionaries[code];
            if (!dictionary) {
                state.dictionaries = {
                    ...state.dictionaries,
                    [code]: list,
                };
                return;
            }

            const items = [...dictionary, ...list];

            state.dictionaries = {
                ...state.dictionaries,
                [code]: items,
            };
        },
    },

    actions: {
        /** PROFILES */
        async getProfiles(ctx, params) {
            const { url } = getApi(ctx);

            const { data, headers } = await axios.get(url("api/profile/index"), {
                params,
            });

            return {
                items: data,
                ...getPagination(headers),
            };
        },
        async getProfile(ctx, id) {
            const { url } = getApi(ctx);

            const { data } = await axios.get(url("api/profile/view", { id }));

            return data;
        },
        async createProfile(ctx, params) {
            const { url } = getApi(ctx);

            const { data } = await axios.post(url("api/profile/create"), params);

            return data;
        },
        async updateProfile(ctx, params) {
            const { url } = getApi(ctx);
            const { id, ...profileParams } = params;

            const { data } = await axios.post(url("api/profile/update", { id }), profileParams);

            return data;
        },
        async deleteProfile(ctx, id) {
            const { url } = getApi(ctx);

            const { data } = await axios.delete(url("api/profile/delete", { id }));

            return data;
        },
        /** TABS */
        async getTabs(ctx) {
            const { url } = getApi(ctx);

            const { data } = await axios.get(url("api/profile-tab/index"));

            return data;
        },
        async getTab(ctx, id) {
            const { url } = getApi(ctx);

            const { data } = await axios.get(url("api/profile-tab/view", { id }));

            return data;
        },
        async createTab(ctx, params) {
            const { url } = getApi(ctx);

            const { profileId, name, profileSection } = params;

            const { data } = await axios.post(url("api/profile-tab/create"), {
                name,
                type: profileSection,
                profile_id: profileId,
            });

            return data;
        },
        async updateTab(ctx, params) {
            const { url } = getApi(ctx);

            const { id, profileId, name, profileSection } = params;

            const { data } = await axios.put(url("api/profile-tab/update", { id }), {
                name,
                type: profileSection,
                profile_id: profileId,
            });

            return data;
        },
        async deleteTab(ctx, id) {
            const { url } = getApi(ctx);

            const { data } = await axios.delete(url("api/profile-tab/delete", { id }));

            return data;
        },
        async sortTabs(ctx, order) {
            const { url } = getApi(ctx);
            const params = order.map((tabId, i) => ({ id: tabId, sort: i + 1 }));

            const { data } = await axios.put(url("api/profile-tab/update-sort"), params);

            return data;
        },

        /** DICTIONARIES */
        async getDictionary(ctx, params) {
            const { state, url, commit } = getApi(ctx);

            const { code, fieldId } = params;

            if (code && code in state.dictionaries) {
                return state.dictionaries[code];
            }

            if (fieldId && fieldId in state.dictionaries) {
                return state.dictionaries[fieldId];
            }

            const { data } = await axios
                .get(url("api/dictionary/index", code ? { code } : { field_id: fieldId }))
                .catch((err) => {
                    console.error(err.response);
                    return { data: [] };
                });

            const list = formatDictionary(data);

            commit("setDictionary", { code: code || fieldId, list });
            return list;
        },
        async updateDictionary(ctx, params) {
            const { url, commit } = getApi(ctx);
            const { code, fieldId, dictionary } = params;
            const { data } = await axios.post(
                url("api/dictionary/save", code ? { code } : { field_id: fieldId }),
                dictionary,
            );
            const list = formatDictionary(data);
            commit("setDictionary", { code: code || fieldId, list });
            return list;
        },
        async addDictionaryItems(ctx, params) {
            const { code, list } = params;
            const { commit } = getApi(ctx);
            commit("addDictionaryItems", { code, list });
        },

        /** FIELDS */
        async getTabFields(ctx, tabId) {
            const { url } = getApi(ctx);

            const { data } = await axios.get(url("api/profile-field/index", { id: tabId }));

            return data;
        },
        async getField(ctx, id) {
            const { url } = getApi(ctx);

            const { data } = await axios.get(url("api/profile-field/view", { id }));

            return data;
        },
        async createField(ctx, params) {
            const { url } = getApi(ctx);

            const { tabId, ...fieldParams } = params;

            const { data } = await axios.post(url("api/profile-field/create"), {
                tab: tabId,
                ...fieldParams,
            });

            return data;
        },

        async updateField(ctx, params) {
            const { url } = getApi(ctx);

            const { id, tabId, ...fieldParams } = params;

            const { data } = await axios.put(url("api/profile-field/update", { id }), {
                tab: tabId,
                ...fieldParams,
            });

            return data;
        },
        async deleteField(ctx, id) {
            const { url } = getApi(ctx);

            const { data } = await axios.delete(url("api/profile-field/delete", { id }));

            return data;
        },
        async moveFieldToTab(ctx, params) {
            const { url } = getApi(ctx);

            const { id, newTabId } = params;

            const { data } = await axios.put(url("api/profile-field/update", { id }), {
                profile_tab_id: newTabId,
            });

            return data;
        },
        async sortFields(ctx, order) {
            const { url } = getApi(ctx);

            const { data } = await axios.put(url("api/profile-field/update-sort"), order);

            return data;
        },
        /** OTHER */
        async copyProfileSection(ctx, params) {
            const { url } = getApi(ctx);

            const { id, from, to } = params;

            const { data, error } = await axios({
                method: "post",
                url: url("api/profile/copy", {
                    id,
                    type: from,
                    target: to,
                }),
            }).catch(() => ({ error: true }));

            if (error) {
                return {
                    success: false,
                    error,
                };
            }

            return {
                success: true,
                message: data,
            };
        },
        async getCustomFields(ctx) {
            const { url } = getApi(ctx);

            const { data } = await axios.get(url("api/profile-field/custom-list"));

            return data;
        },
    },
};
